import { Controller } from "@hotwired/stimulus";

// Collapsed "2 vuxna" summary field that expands into the real passenger
// selects (adults/children/youths) on click -- Figma shows the closed
// summary field but the opened state wasn't captured in the reference
// screenshots, so this popover shell is a reasonable interim built from the
// same expand-on-click pattern used everywhere else in the design (baggage
// dropdowns, "Lägg till bonuskort", etc.), not a pixel-verified match.
// The underlying <select> elements and their mutual-exclusivity behavior
// (adults/children vs youths) are unchanged real business logic -- this
// controller only handles the summary text and open/close, no field logic.
// Adults+children+infants combined max 9, youths a separate pool maxed at
// 9 on its own and mutually exclusive with the other three -- both real,
// backend-enforced constraints (see the old, now-dead
// searchfield_functions.ts's selectmenuchange handlers, which did the
// same forcing-to-zero on the legacy jQuery-UI selects this replaced).
const STANDARD_TOTAL_MAX = 9;

export default class extends Controller {
  static targets = [
    "summary", "panel", "adults", "children", "infants", "youths",
    "standardFields", "youthFields", "standardTab", "youthTab",
  ];
  static values = { youthMode: Boolean };

  connect() {
    this.boundClose = this.closeOnOutsideClick.bind(this);
    document.addEventListener("click", this.boundClose);
    // Not the default -- only starts in youth mode when a deeplink already
    // searched with youths (youthModeValue, set server-side from
    // @sp.youths -- see the searchfield partials).
    this.setMode(this.youthModeValue, { silent: true });
    this.capStandardFields();
    this.updateSummary();
  }

  disconnect() {
    document.removeEventListener("click", this.boundClose);
  }

  toggle(event) {
    event.stopPropagation();
    this.panelTarget.classList.toggle("hidden");
  }

  close() {
    this.panelTarget.classList.add("hidden");
  }

  closeOnOutsideClick(event) {
    if (!this.element.contains(event.target)) this.close();
  }

  showStandard() {
    this.setMode(false);
  }

  showYouth() {
    this.setMode(true);
  }

  // silent: true skips the "force adults back to 1" UX nicety on the
  // standard side -- used only on initial connect(), where the
  // just-rendered values (from @sp, a real search or its defaults) should
  // be left as the server sent them. Zeroing whichever side is NOT active
  // always happens regardless, silent or not: adults/children/infants and
  // youths combining is a hard, backend-enforced constraint, not a UX
  // nicety, so even a page that loads straight into youth mode (a
  // deeplink) can't be left with both sides non-zero.
  setMode(youthMode, { silent = false } = {}) {
    if (!this.hasStandardFieldsTarget || !this.hasYouthFieldsTarget) return;

    this.standardFieldsTarget.classList.toggle("hidden", youthMode);
    this.youthFieldsTarget.classList.toggle("hidden", !youthMode);
    if (this.hasStandardTabTarget) {
      this.standardTabTarget.classList.toggle("bg-white", !youthMode);
      this.standardTabTarget.classList.toggle("shadow-sm", !youthMode);
      this.standardTabTarget.classList.toggle("text-gray-900", !youthMode);
      this.standardTabTarget.classList.toggle("text-gray-500", youthMode);
    }
    if (this.hasYouthTabTarget) {
      this.youthTabTarget.classList.toggle("bg-white", youthMode);
      this.youthTabTarget.classList.toggle("shadow-sm", youthMode);
      this.youthTabTarget.classList.toggle("text-gray-900", youthMode);
      this.youthTabTarget.classList.toggle("text-gray-500", !youthMode);
    }

    if (youthMode) {
      if (this.hasChildrenTarget) this.childrenTarget.value = "0";
      if (this.hasInfantsTarget) this.infantsTarget.value = "0";
      this.adultsTarget.value = "0";
    } else {
      if (this.hasYouthsTarget) this.youthsTarget.value = "0";
      if (!silent && parseInt(this.adultsTarget.value, 10) === 0) this.adultsTarget.value = "1";
    }

    if (!silent) {
      this.capStandardFields();
      this.updateSummary();
    }
  }

  // Dynamically caps each of adults/children/infants' own <select> options
  // to whatever's left of the shared 9-passenger budget given the *other*
  // two fields' current values -- e.g. 5 adults already picked leaves
  // children and infants capped at 4 each, not the full 0-8 range their
  // options list ships with. Clamps the field's own current value down
  // too, on the rare chance it's already past its new cap (only possible
  // if two fields change in the same tick, e.g. showStandard() zeroing
  // several at once).
  //
  // Infants get a second, real-world cap on top of the shared budget:
  // never more infants than adults (a lap infant needs an adult to sit
  // on), so reducing adults below the current infant count must also
  // clamp infants down -- capSelectOptions' own clamp-down handles that
  // once given the smaller of the two limits.
  capStandardFields() {
    const adults = parseInt(this.adultsTarget.value, 10) || 0;
    const children = this.hasChildrenTarget ? parseInt(this.childrenTarget.value, 10) || 0 : 0;
    const infants = this.hasInfantsTarget ? parseInt(this.infantsTarget.value, 10) || 0 : 0;

    this.capSelectOptions(this.adultsTarget, STANDARD_TOTAL_MAX - children - infants);
    if (this.hasChildrenTarget) this.capSelectOptions(this.childrenTarget, STANDARD_TOTAL_MAX - adults - infants);
    if (this.hasInfantsTarget) this.capSelectOptions(this.infantsTarget, Math.min(STANDARD_TOTAL_MAX - adults - children, adults));
  }

  capSelectOptions(select, max) {
    const clampedMax = Math.max(0, max);
    Array.from(select.options).forEach((option) => {
      const disable = parseInt(option.value, 10) > clampedMax;
      option.disabled = disable;
      option.hidden = disable;
    });
    if ((parseInt(select.value, 10) || 0) > clampedMax) select.value = String(clampedMax);
  }

  updateSummary(event) {
    // Any adults/children/infants change can shrink an unrelated field's
    // own remaining budget (capStandardFields reads all three every time),
    // so recompute the caps on every one of their change events, not just
    // the field that actually changed.
    if (event && ["adults", "children", "infants"].includes(event.target?.dataset?.passengerPickerTarget)) {
      this.capStandardFields();
    }

    const adults = parseInt(this.adultsTarget.value, 10) || 0;
    const children = this.hasChildrenTarget ? parseInt(this.childrenTarget.value, 10) || 0 : 0;
    const infants = this.hasInfantsTarget ? parseInt(this.infantsTarget.value, 10) || 0 : 0;
    const youths = this.hasYouthsTarget ? parseInt(this.youthsTarget.value, 10) || 0 : 0;

    const d = this.element.dataset;

    if (youths > 0 && adults === 0 && children === 0 && infants === 0) {
      this.summaryTarget.textContent = `${youths} ${youths === 1 ? d.passengerPickerSummaryYouthSingular : d.passengerPickerSummaryYouthPlural}`;
      return;
    }

    const parts = [`${adults} ${adults === 1 ? d.passengerPickerSummaryAdultSingular : d.passengerPickerSummaryAdultPlural}`];
    if (children > 0) parts.push(`${children} ${children === 1 ? d.passengerPickerSummaryChildSingular : d.passengerPickerSummaryChildPlural}`);
    if (infants > 0) parts.push(`${infants} ${infants === 1 ? d.passengerPickerSummaryInfantSingular : d.passengerPickerSummaryInfantPlural}`);
    this.summaryTarget.textContent = parts.join(", ");
  }
}
